import { Hub, type HubCapsule } from "@aws-amplify/core";
import type React from "react";
import { type FC, useCallback, useContext, useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AdminRoute from "./components/AdminRoute";
import Layout from "./components/Layout/Layout";
import NoHeadLayout from "./components/NoHeadLayout/NoHeadLayout";
import TenantAdminRoute from "./components/TenantAdminRoute";
import { AuthContext } from "./hooks/authProvider/authProvider";
import { usePublicFeatureFlags } from "./hooks/usePublicFeatureFlags";
import AboutPage from "./routes/AboutPage";
import {
  DeliveriesPage,
  DeliveryDetailPage,
  DeliveryEditPage,
  DeliveryNewPage,
} from "./routes/DeliveryPage";
import { EmailDetailPageContainer } from "./routes/emails/EmailDetailPage";
import SearchEmailsPage from "./routes/emails/SearchEmailsPage";
import TemplateSamplePage from "./routes/emails/TemplateSamplePage";
import HomePage from "./routes/HomePage";
import InvitationPage from "./routes/InvitationPage";
import LandingPage from "./routes/LandingPage";
import MatterPage from "./routes/MatterPage/MatterPage";
import MatchingPage from "./routes/matching/MatchingPage";
import MatchingResultsPage from "./routes/matching/MatchingResultsPage";
import OnboardingPage from "./routes/OnboardingPage";
import ProfilePage from "./routes/ProfilePage";
import RedirectPage from "./routes/RedirectPage";
import ResetPassWordPage from "./routes/ResetPassWordPage";
import SignInPage from "./routes/SignInPage";
import SignUpPage from "./routes/SignUpPage";
import SamplesPage from "./routes/samples/SamplesPage";
import InvitationsSettingsPage from "./routes/settings/InvitationsPage";
import MatchingConditionFormPage from "./routes/settings/MatchingConditionFormPage";
import MatchingConditionsPage from "./routes/settings/MatchingConditionsPage";
import PresetDetailPage from "./routes/settings/PresetDetailPage";
import PresetFormPage from "./routes/settings/PresetFormPage";
import PresetsPage from "./routes/settings/PresetsPage";
import SlackOAuthCallbackPage from "./routes/settings/SlackOAuthCallbackPage";
import TenantSettingsPage from "./routes/settings/TenantPage";

/**
 * @return {Router} ルーター
 */
function App() {
  return (
    <Router>
      <Routes>
        <Route
          path="/signup"
          element={
            <NoHeadLayout>
              <SignUpPage />
            </NoHeadLayout>
          }
        />
        <Route
          path="/login"
          element={
            <NoHeadLayout>
              <SignInPage />
            </NoHeadLayout>
          }
        />
        <Route
          path="/reset-pass-word"
          element={
            <NoHeadLayout>
              <ResetPassWordPage />
            </NoHeadLayout>
          }
        />
        <Route
          path="/invitations/:token"
          element={
            <NoHeadLayout>
              <InvitationPage />
            </NoHeadLayout>
          }
        />
        <Route path="/lp" element={<LpRoute />} />
        <Route
          path="/settings/slack/callback"
          element={<SlackOAuthCallbackPage />}
        />
        <Route path="/" element={<RootRoute />} />
        <Route
          path="/*"
          element={
            <AuthRedirect>
              <Layout>
                <Routes>
                  <Route path="/redirect" element={<RedirectPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route
                    path="/profile"
                    element={
                      <PrivateRoute>
                        <ProfilePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/matter"
                    element={
                      <PrivateRoute>
                        <MatterPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/search/:id"
                    element={
                      <PrivateRoute>
                        <EmailDetailPageContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/search"
                    element={
                      <PrivateRoute>
                        <SearchEmailsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/template-sample"
                    element={
                      <PrivateRoute>
                        <TemplateSamplePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/matching/results"
                    element={
                      <PrivateRoute>
                        <MatchingResultsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/matching/free"
                    element={
                      <PrivateRoute>
                        <MatchingPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/samples"
                    element={
                      <PrivateRoute>
                        <SamplesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries"
                    element={
                      <PrivateRoute>
                        <DeliveriesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries/new"
                    element={
                      <PrivateRoute>
                        <DeliveryNewPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries/:id"
                    element={
                      <PrivateRoute>
                        <DeliveryDetailPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries/:id/edit"
                    element={
                      <PrivateRoute>
                        <DeliveryEditPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/tenant"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <TenantSettingsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/invitations"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <InvitationsSettingsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/matching/conditions"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <MatchingConditionsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/matching/conditions/new"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <MatchingConditionFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/matching/conditions/:id/edit"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <MatchingConditionFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets/new"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets/:id/edit"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets/:id"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetDetailPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      <PrivateRoute>
                        <AdminRoute />
                      </PrivateRoute>
                    }
                  />
                  {/* <Route path="/users" element={<PrivateRoute><UsersPage /></PrivateRoute>} /> */}
                  <Route
                    path="/dashboard"
                    element={
                      <PrivateRoute>
                        <HomePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <AuthRequiredRoute>
                        <OnboardingPage />
                      </AuthRequiredRoute>
                    }
                  />
                </Routes>
              </Layout>
            </AuthRedirect>
          }
        />
      </Routes>
    </Router>
  );
}

/**
 * ルートパス（/）の振り分け
 * 認証済み → /dashboard、未認証 → LP（feature flag 有効時）またはログイン画面
 * @return {JSX.Element} 認証状態に応じたコンポーネント
 */
const RootRoute: FC = () => {
  const { currentUser, isLoadingUser } = useContext(AuthContext);
  const { flags, loading: flagsLoading } = usePublicFeatureFlags();
  const lpEnabled = flags["ui.landing_page"] === true;

  if (isLoadingUser || flagsLoading) {
    return <div>loading...</div>;
  }

  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  if (lpEnabled) {
    return <LandingPage />;
  }

  return <Navigate to="/login" replace />;
};

/**
 * /lp ルート: feature flag で LP の表示を制御
 * @return {JSX.Element} LP または 404
 */
const LpRoute: FC = () => {
  const { flags, loading } = usePublicFeatureFlags();
  const lpEnabled = flags["ui.landing_page"] === true;

  if (loading) {
    return <div>loading...</div>;
  }

  if (lpEnabled) {
    return <LandingPage />;
  }

  return <Navigate to="/login" replace />;
};

/**
 * 認証のみ必須のルート（テナントチェックなし）
 * オンボーディングページ用
 * @return {JSX.Element} 認証済みの場合は子要素、未認証の場合はリダイレクト
 */
const AuthRequiredRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoadingUser, authError } = useContext(AuthContext);

  if (isLoadingUser) {
    return <div>loading...</div>;
  }

  if (authError) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * 認証＋テナント必須のルート
 * Python Backend 使用時: tenants が空なら /onboarding へリダイレクト
 * TS Backend 使用時: tenants が undefined なのでスキップ
 * @return {JSX.Element} 認証・テナント済みの場合は子要素、それ以外はリダイレクト
 */
const PrivateRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoadingUser, authError } = useContext(AuthContext);

  if (isLoadingUser) {
    return <div>loading...</div>;
  }

  if (authError) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Python Backend: tenants が存在するが空の場合はオンボーディングへ
  // TS Backend: tenants が undefined なのでこのチェックはスキップ
  if (currentUser.tenants !== undefined && currentUser.tenants.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

const AuthRedirect: FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const redirectToPreviousPageAfterLogin = useCallback(
    (data: HubCapsule<"auth", any>) => {
      const { payload } = data;
      switch (payload.event) {
        case "customOAuthState":
          navigate(decodeURIComponent(payload.data));
          break;
        case "signIn_failure":
          navigate("/dashboard");
          break;
      }
    },
    [navigate],
  );

  useEffect(() => {
    const listener = Hub.listen("auth", redirectToPreviousPageAfterLogin);
    return () => {
      listener();
    };
  }, [redirectToPreviousPageAfterLogin]);

  return <>{children}</>;
};

export default App;
